/** Paywall abbonamento annuale e step firma documenti legali. */
import { useEffect, useState } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import {
  Building2,
  CheckCircle2,
  CreditCard,
  FileSignature,
  Loader2,
  Shield,
  Sparkles,
} from "lucide-react";
import { toast } from "sonner";

import { endpoints, type SigningTemplateData } from "@/api/client";
import { BaseVoucherCard } from "@/components/BaseVoucherCard";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { FormField } from "@/components/forms";
import { exitAzienda } from "@/lib/azienda";

const PLAN_FEATURES = [
  "Registri e movimenti verso RENTRI",
  "Formulari e xFIR",
  "5 CER preferiti inclusi",
  "Fino a 1000 movimenti / 1000 xFIR all'anno",
];

export function Abbonamento() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const canceled = params.get("canceled") === "1";
  const success = params.get("success") === "1";
  const stepFirma = params.get("step") === "firma" || success;

  const statusQ = useQuery({
    queryKey: ["billing-status"],
    queryFn: endpoints.billing.status,
    refetchOnMount: "always",
  });

  useEffect(() => {
    if (success) {
      void endpoints.billing.sync().then(() => {
        qc.invalidateQueries({ queryKey: ["billing-status"] });
      });
    }
  }, [success, qc]);

  const [form, setForm] = useState<SigningTemplateData>({
    nome_azienda: "",
    indirizzo: "",
    piva: "",
    nome_rappresentante: "",
  });

  const checkoutMut = useMutation({
    mutationFn: () => {
      const origin = window.location.origin;
      return endpoints.billing.checkout({
        kind: "suite_annual",
        success_url: `${origin}/abbonamento?success=1`,
        cancel_url: `${origin}/abbonamento?canceled=1`,
      });
    },
    onSuccess: (data) => {
      window.location.href = data.url;
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Errore checkout"),
  });

  const signMut = useMutation({
    mutationFn: () => endpoints.billing.signingRequests(form),
    onSuccess: (r) => {
      toast.success(r.message);
      qc.invalidateQueries({ queryKey: ["billing-status"] });
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Errore invio firma"),
  });

  const s = statusQ.data;
  const showFirma =
    stepFirma && s?.subscription_active && !s.signing.canUseApp;

  const gestoreMut = useMutation({
    mutationFn: () => endpoints.gestore.attiva(),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ["billing-status"] });
      navigate("/gestore", { replace: true });
    },
    onError: (e: unknown) => toast.error(e instanceof Error ? e.message : "Errore"),
  });

  // Il gestore sul proprio account non compra l'abbonamento singolo: va alla sua dashboard.
  useEffect(() => {
    if (s?.is_gestore && !s.managed_by) navigate("/gestore", { replace: true });
  }, [s?.is_gestore, s?.managed_by, navigate]);

  // Dentro un'azienda gestita il modulo di firma parte dai dati dell'azienda.
  useEffect(() => {
    if (s?.azienda) {
      setForm((f) => ({
        ...f,
        nome_azienda: f.nome_azienda || s.azienda?.ragione_sociale || "",
        piva: f.piva || s.azienda?.piva || "",
      }));
    }
  }, [s?.azienda]);

  if (statusQ.isLoading || (s?.is_gestore && !s.managed_by)) {
    return (
      <div className="flex min-h-[60vh] items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
      </div>
    );
  }

  if (s?.can_use_app) {
    return (
      <div className="mx-auto max-w-2xl px-4 py-16">
        <div className="text-center">
          <CheckCircle2 className="mx-auto h-14 w-14 text-emerald-600" />
          <h1 className="mt-4 text-2xl font-semibold">Tutto pronto</h1>
          <p className="mt-2 text-muted-foreground">
            Abbonamento e documenti legali sono in ordine. Puoi usare RENTRI Suite.
          </p>
          <Button asChild className="mt-8">
            <Link to="/">Vai alla dashboard</Link>
          </Button>
        </div>
        <BaseVoucherCard id="rentri-base" className="mt-10" />
      </div>
    );
  }

  return (
    <div className="min-h-full bg-gradient-to-b from-slate-50 to-white dark:from-slate-950 dark:to-slate-900">
      <div className="mx-auto max-w-2xl px-4 py-10 sm:py-14">
        <div className="mb-8 text-center">
          <Badge variant="secondary" className="mb-3">
            RENTRI Suite
          </Badge>
          <h1 className="text-3xl font-semibold tracking-tight">
            {showFirma ? "Firma i documenti" : "Attiva il tuo abbonamento"}
          </h1>
          <p className="mt-2 text-muted-foreground">
            {showFirma
              ? "Ultimo passo prima di operare sui registri: nomina conservazione e contratto GDPR."
              : "Piano annuale con tutto il necessario per gestire registri, movimenti e formulari."}
          </p>
        </div>

        {s?.managed_by && (
          <div className="mb-6 flex flex-wrap items-center justify-between gap-2 rounded-lg border border-sky-200 bg-sky-50 px-4 py-3 text-sm text-sky-900">
            <span className="flex items-center gap-2">
              <Building2 className="h-4 w-4" />
              Stai lavorando per: <strong>{s.azienda?.ragione_sociale ?? "azienda gestita"}</strong>
              {s.azienda?.email_firma && (
                <span className="text-sky-800/80">· i documenti saranno inviati a {s.azienda.email_firma}</span>
              )}
            </span>
            <Button size="sm" variant="outline" onClick={() => exitAzienda()}>
              Torna alle mie aziende
            </Button>
          </div>
        )}

        {canceled && (
          <div className="mb-6 rounded-lg border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            Pagamento annullato. Puoi riprovare quando vuoi.
          </div>
        )}

        {!showFirma && (
          <Card className="border-slate-200/80 shadow-sm">
            <CardHeader>
              <div className="flex items-start justify-between gap-4">
                <div>
                  <CardTitle className="flex items-center gap-2 text-xl">
                    <Sparkles className="h-5 w-5 text-primary" />
                    Abbonamento annuale
                  </CardTitle>
                  <CardDescription className="mt-1">
                    Pagamento sicuro con carta (Stripe)
                  </CardDescription>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-bold tabular-nums">€69 + IVA</p>
                  <p className="text-xs text-muted-foreground">/ anno (€84,18 IVA inclusa)</p>
                </div>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              <ul className="space-y-2 text-sm">
                {PLAN_FEATURES.map((f) => (
                  <li key={f} className="flex items-start gap-2">
                    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0 text-emerald-600" />
                    <span>{f}</span>
                  </li>
                ))}
              </ul>
              <p className="text-xs text-muted-foreground">
                CER aggiuntivi: pacchetti da 5 CER a €20 + IVA all&apos;anno, acquistabili dalla
                sezione CER attivi; si rinnovano insieme all&apos;abbonamento.
              </p>
              <Button
                className="w-full"
                size="lg"
                onClick={() => checkoutMut.mutate()}
                disabled={checkoutMut.isPending || s?.subscription_active}
              >
                {checkoutMut.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <CreditCard className="mr-2 h-4 w-4" />
                )}
                {s?.subscription_active ? "Abbonamento attivo" : "Paga con carta — €69 + IVA/anno"}
              </Button>
              {s?.subscription_active && (
                <p className="text-center text-sm text-emerald-700">
                  Pagamento ricevuto. Completa la firma dei documenti qui sotto.
                </p>
              )}
            </CardContent>
          </Card>
        )}

        {!showFirma && !s?.subscription_active && !s?.managed_by && (
          <Card className="mt-6 border-sky-200/80 bg-sky-50/30 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <Building2 className="h-5 w-5 text-sky-700" />
                Gestisci più aziende?
              </CardTitle>
              <CardDescription>
                Consulenti e studi: registrazione gratuita, poi attivi le aziende che segui con un
                unico abbonamento. Il prezzo per azienda scende con il numero di aziende: €69 (1–10),
                €59 (11–30), €50 (31–100) + IVA all&apos;anno, scadenza comune e un solo pagamento
                al rinnovo. Oltre 100 aziende offerta su richiesta.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <Button
                variant="outline"
                className="w-full"
                onClick={() => gestoreMut.mutate()}
                disabled={gestoreMut.isPending}
              >
                {gestoreMut.isPending ? (
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                ) : (
                  <Building2 className="mr-2 h-4 w-4" />
                )}
                Continua come gestore di più aziende
              </Button>
            </CardContent>
          </Card>
        )}

        {(showFirma || s?.subscription_active) && !s?.signing.canUseApp && (
          <Card className="mt-6 border-amber-200/80 bg-amber-50/30 shadow-sm">
            <CardHeader>
              <CardTitle className="flex items-center gap-2 text-lg">
                <FileSignature className="h-5 w-5 text-amber-700" />
                Documenti da firmare
              </CardTitle>
              <CardDescription>
                Riceverai due email con il link per firmare digitalmente.
              </CardDescription>
            </CardHeader>
            <CardContent>
              <form
                className="space-y-4"
                onSubmit={(e) => {
                  e.preventDefault();
                  signMut.mutate();
                }}
              >
                <FormField label="Nome azienda / ente" required>
                  <Input
                    required
                    value={form.nome_azienda}
                    onChange={(e) => setForm((f) => ({ ...f, nome_azienda: e.target.value }))}
                  />
                </FormField>
                <FormField label="Indirizzo sede legale" required>
                  <Input
                    required
                    value={form.indirizzo}
                    onChange={(e) => setForm((f) => ({ ...f, indirizzo: e.target.value }))}
                  />
                </FormField>
                <FormField label="P.IVA" required>
                  <Input
                    required
                    value={form.piva}
                    onChange={(e) => setForm((f) => ({ ...f, piva: e.target.value }))}
                  />
                </FormField>
                <FormField label="Rappresentante legale" required>
                  <Input
                    required
                    value={form.nome_rappresentante}
                    onChange={(e) =>
                      setForm((f) => ({ ...f, nome_rappresentante: e.target.value }))
                    }
                  />
                </FormField>
                {(s?.signing.pendingNomina || s?.signing.pendingContratto) && (
                  <p className="text-sm text-amber-800">
                    Richiesta inviata: controlla la posta e completa le firme in sospeso.
                  </p>
                )}
                <Button type="submit" className="w-full" disabled={signMut.isPending}>
                  {signMut.isPending ? (
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  ) : (
                    <Shield className="mr-2 h-4 w-4" />
                  )}
                  Invia richieste di firma
                </Button>
              </form>
              <Button
                variant="ghost"
                className="mt-3 w-full text-sm"
                onClick={() => statusQ.refetch()}
              >
                Ho firmato — aggiorna stato
              </Button>
            </CardContent>
          </Card>
        )}

        {s?.subscription_active && <BaseVoucherCard id="rentri-base" className="mt-6" />}

        <p className="mt-8 text-center text-sm text-muted-foreground">
          <Link to="/impostazioni" className="underline hover:text-foreground">
            Impostazioni
          </Link>
          {" · "}
          <Link to="/login" className="underline hover:text-foreground">
            Esci
          </Link>
        </p>
      </div>
    </div>
  );
}
